import type { FC } from "react";
import { Activity, Announcement01, Cube01, BarChartSquare02, Beaker02, Building02, CurrencyDollarCircle, Download01, FileCheck02, FileShield02, Globe01, Inbox01, Mail01, MessageQuestionCircle, SearchLg, Send01, Star01, Ticket01, TrendUp01, UserPlus01, Users01, Users02 } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import { isAgentsEnabled } from "@/lib/agents-flag";
import type { NavItemType } from "@/components/application/app-navigation/config";

export interface NavGroup {
  label: string;
  /** Icon for the collapsed (slim) rail entry that represents this group. */
  icon: FC<{ className?: string }>;
  items: NavItemType[];
  /**
   * Render this group as an expandable `<details>` accordion (summary = label +
   * icon, children = items) instead of an always-open subheading section.
   */
  collapsible?: boolean;
}

/** Console navigation, grouped. Labels are translated; hrefs map to routes. */
export function useNavGroups(): NavGroup[] {
  const { t } = useTranslation();
  return [
    {
      label: t("shell.groupOverview"),
      icon: BarChartSquare02,
      items: [
        { label: t("shell.nav.dashboard"), href: "/", icon: BarChartSquare02 },
        { label: t("shell.nav.customers"), href: "/customers", icon: Building02 },
        { label: t("shell.nav.users"), href: "/users", icon: Users02 },
        { label: t("shell.nav.revenue"), href: "/revenue", icon: CurrencyDollarCircle },
        { label: t("shell.nav.usage"), href: "/usage", icon: Activity },
        ...(isAgentsEnabled() ? [{ label: t("shell.nav.agents"), href: "/agents", icon: Cube01 }] : []),
      ],
    },
    {
      label: t("shell.groupData"),
      icon: Globe01,
      items: [
        { label: t("shell.nav.newsletter"), href: "/newsletter", icon: Mail01 },
        { label: t("shell.nav.applications"), href: "/applications", icon: FileCheck02 },
        { label: t("shell.nav.leads"), href: "/leads", icon: Inbox01 },
        { label: t("shell.nav.gated"), href: "/gated-content", icon: FileShield02 },
        { label: t("shell.nav.downloadNotify"), href: "/download-notify", icon: Download01 },
        { label: t("shell.nav.helpFeedback"), href: "/help-feedback", icon: MessageQuestionCircle },
      ],
    },
    {
      label: t("shell.groupOnboarding"),
      icon: UserPlus01,
      items: [
        { label: t("shell.nav.invites"), href: "/invites", icon: Send01 },
        { label: t("shell.nav.accessRequests"), href: "/access-requests", icon: Announcement01 },
        { label: t("shell.nav.signupCodes"), href: "/signup-codes", icon: Ticket01 },
      ],
    },
    {
      label: t("shell.groupGrowth"),
      icon: TrendUp01,
      items: [
        { label: t("shell.nav.seo"), href: "/seo", icon: SearchLg },
        { label: t("shell.nav.reviewHub"), href: "/reviews", icon: Star01 },
      ],
    },
    {
      label: t("shell.groupPlayground"),
      icon: Beaker02,
      collapsible: true,
      items: [{ label: t("shell.nav.dataTypes"), href: "/playground/data-types", icon: Beaker02 }],
    },
    {
      label: t("shell.groupAdmin"),
      icon: Users01,
      items: [{ label: t("shell.nav.members"), href: "/members", icon: Users01 }],
    },
  ];
}

/**
 * Collapsed (slim) rail: one entry per group, carrying the group's children as
 * sub-items so the Framer-Motion hover-secondary panel reveals the full labels.
 */
export function useNavRail(): (NavItemType & { icon: FC<{ className?: string }> })[] {
  return useNavGroups().map((group) => ({
    label: group.label,
    href: group.items[0]?.href ?? "/",
    icon: group.icon,
    items: group.items.map((item) => ({ label: item.label, href: item.href!, icon: item.icon })),
  }));
}
